import { useCallback, useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import {
  Brand,
  BrandResponse,
  CreateBrandParams,
  EnumOption,
  BrandExternalVetting,
  RequestBrandExternalVettingRequest,
  useCreateBrandApi,
  useGetBrandApi,
  useGetBrandRelationshipsApi,
  useGetEntityTypesApi,
  useGetVerticalsApi,
  useRequestBrandExternalVettingApi,
  useSearchBrandExternalVettingApi,
  useSearchBrandApi,
} from 'src/api/campaignRegistry';
import { handleApiError, focusFieldByName } from 'src/api/campaignRegistry/apiErrorParser';
import { useSaveCompanyBrandInfoApi } from '../api';
import { AdminCompany } from '../types';
import {
  BrandFormState,
  BrandInfoTabProps,
  BrandInfoTabViewModel,
  VettingRow,
  VettingPartner,
  VettingType,
} from '../types';
import { useFormErrors, useFormState } from '../components/EditCompanyView/shared/hooks';
import { formatEin, isValidEin, processEnumOptions } from '../helpers';

const NOT_AVAILABLE = 'N/A';

const normalizeEntityType = (value: string): string => {
  return value.trim().toLowerCase().replace(/[\s_-]/g, '');
};

const isSoleProprietorEntityType = (entityType: string, entityTypes: EnumOption[]): boolean => {
  if (!entityType) return false;

  const normalizedValue = normalizeEntityType(entityType);
  if (normalizedValue === 'soleproprietor') return true;

  const matchingOption = entityTypes.find(option => option.value === entityType);
  if (!matchingOption?.label) return false;

  return normalizeEntityType(matchingOption.label) === 'soleproprietor';
};

const getBrandId = (brand: Brand): string => {
  return brand.brandId || '';
};

const formatDate = (value?: string | null): string => {
  if (!value) return NOT_AVAILABLE;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return NOT_AVAILABLE;
  return date.toLocaleDateString();
};

const formatVettingPartner = (evpId?: string | null): string => {
  if (!evpId) return NOT_AVAILABLE;
  if (evpId.toUpperCase() === 'AEGIS') return 'Aegis Mobile';
  return evpId;
};

const mapVettingRows = (vettings: BrandExternalVetting[]): VettingRow[] => {
  return vettings.map(vetting => ({
    id: vetting.vettingId || `${vetting.evpId}-${vetting.vettingClass}-${vetting.createDate}`,
    vettingType: vetting.vettingClass || NOT_AVAILABLE,
    vettingPartner: formatVettingPartner(vetting.evpId),
    vettingStatus: vetting.vettingStatus || NOT_AVAILABLE,
    requestedDate: formatDate(vetting.createDate),
    validUntil: formatDate(vetting.validUntil || vetting.expirationDate),
    outcome: vetting.vettingScore || vetting.reasons || NOT_AVAILABLE,
  }));
};

const populateFormFromBrand = (brand: Brand): Partial<BrandFormState> => {
  return {
    brandId: getBrandId(brand),
    entityType: brand.entityType,
    companyName: brand.companyName,
    ein: formatEin(brand.ein),
    einIssuingCountry: brand.einIssuingCountry,
    phone: brand.phone,
    street: brand.street,
    city: brand.city,
    state: brand.state,
    postalCode: brand.postalCode,
    country: brand.country,
    email: brand.email,
    website: brand.website,
    brandRelationship: brand.brandRelationship,
    vertical: brand.vertical,
    mock: brand.mock || false,
    status: brand.status,
    identityStatus: brand.identityStatus,
  };
};

const populateFormFromCompanyBrandInfo = (
  brandInfo: NonNullable<AdminCompany['additionalData']>['brand'],
): Partial<BrandFormState> => {
  if (!brandInfo) return {};
  return {
    entityType: brandInfo.entityType,
    displayName: brandInfo.displayName,
    companyName: brandInfo.companyName,
    ein: formatEin(brandInfo.ein),
    einIssuingCountry: brandInfo.einIssuingCountry,
    phone: brandInfo.phone,
    street: brandInfo.street,
    city: brandInfo.city,
    state: brandInfo.state,
    postalCode: brandInfo.postalCode,
    country: brandInfo.country,
    email: brandInfo.email,
    website: brandInfo.website,
    brandRelationship: brandInfo.brandRelationship,
    vertical: brandInfo.vertical,
    mock: brandInfo.mock || false,
  };
};

const resetBrandFields = (): Partial<BrandFormState> => {
  return {
    brandId: undefined,
    status: undefined,
    identityStatus: undefined,
  };
};

export const useBrandInfoTabController = (params: BrandInfoTabProps): BrandInfoTabViewModel => {
  const { company, refreshCompany } = params;

  const createBrand = useCreateBrandApi();
  const getBrand = useGetBrandApi();
  const searchBrand = useSearchBrandApi();
  const getBrandRelationships = useGetBrandRelationshipsApi();
  const getEntityTypes = useGetEntityTypesApi();
  const getVerticals = useGetVerticalsApi();
  const searchBrandExternalVetting = useSearchBrandExternalVettingApi();
  const requestBrandExternalVetting = useRequestBrandExternalVettingApi();
  const saveCompanyBrandInfo = useSaveCompanyBrandInfoApi();

  const [loading, setLoading] = useState<boolean>(true);
  const [saving, setSaving] = useState<boolean>(false);
  const [searchingBrand, setSearchingBrand] = useState<boolean>(false);
  const [loadingBrandById, setLoadingBrandById] = useState<boolean>(false);
  const [existingBrand, setExistingBrand] = useState<Brand | null>(null);
  const [brandRelationships, setBrandRelationships] = useState<EnumOption[]>([]);
  const [entityTypes, setEntityTypes] = useState<EnumOption[]>([]);
  const [verticals, setVerticals] = useState<EnumOption[]>([]);
  const [vettingRows, setVettingRows] = useState<VettingRow[]>([]);
  const [vettingLoading, setVettingLoading] = useState<boolean>(false);
  const [initialDisplayName, setInitialDisplayName] = useState<string>(company?.name || '');
  const [expandedSections, setExpandedSections] = useState<{
    basicInfo: boolean;
    addressInfo: boolean;
    vettingDetails: boolean;
  }>({
    basicInfo: false,
    addressInfo: false,
    vettingDetails: false,
  });
  const [applyForVettingDialogOpen, setApplyForVettingDialogOpen] = useState<boolean>(false);
  const [vettingNotAllowedDialogOpen, setVettingNotAllowedDialogOpen] = useState<boolean>(false);

  const { fieldErrors, setFieldErrors, clearFieldError } = useFormErrors();

  const { formValues, updateField, updateFields } = useFormState<BrandFormState>(
    {
      companyId: company?.id || '',
      entityType: '',
      displayName: company?.name || '',
      companyName: company?.name || '',
      ein: '',
      einIssuingCountry: 'US',
      phone: company?.phoneNumbers?.[0]?.number || '',
      street: '',
      city: '',
      state: '',
      postalCode: '',
      country: 'US',
      email: company?.billingEmail || '',
      website: '',
      brandRelationship: company?.brandRelationship || '',
      vertical: company?.vertical || '',
      mock: false,
      brandId: undefined,
      status: undefined,
      identityStatus: undefined,
    },
    clearFieldError,
  );

  const showEinValidationError = formValues.ein.trim() !== '' && !isValidEin(formValues.ein);

  useEffect(() => {
    Promise.all([getBrandRelationships(), getEntityTypes(), getVerticals()])
      .then(([brandRelResponse, entityTypeResponse, verticalResponse]) => {
        const brandRelationshipsOptions = processEnumOptions(brandRelResponse.data);
        const entityTypesOptions = processEnumOptions(entityTypeResponse.data);
        const verticalsOptions = processEnumOptions(verticalResponse.data);

        setBrandRelationships(brandRelationshipsOptions);
        setEntityTypes(entityTypesOptions);
        setVerticals(verticalsOptions);
        setLoading(false);
      })
      .catch(error => {
        console.error('Error loading brand form data:', error);
        toast.error('Failed to load form data. Please refresh the page.');
        setBrandRelationships([]);
        setEntityTypes([]);
        setVerticals([]);
        setLoading(false);
      });
  }, [getBrandRelationships, getEntityTypes, getVerticals]);

  const loadExternalVettings = useCallback(
    (brandId: string) => {
      if (!brandId) {
        setVettingRows([]);
        return Promise.resolve();
      }

      setVettingLoading(true);
      return searchBrandExternalVetting(brandId)
        .then(response => {
          const vettings = response.data || [];
          setVettingRows(mapVettingRows(vettings));
        })
        .catch(error => {
          console.error('Error loading external vettings:', error);
          setVettingRows([]);
        })
        .finally(() => {
          setVettingLoading(false);
        });
    },
    [searchBrandExternalVetting],
  );

  const loadBrandById = useCallback(
    (brandId: string) => {
      setLoadingBrandById(true);
      return getBrand(brandId)
        .then(response => {
          const brand = response.data;
          setExistingBrand(brand);
          updateFields(populateFormFromBrand(brand));
          if (brand.brandId) {
            void loadExternalVettings(brand.brandId);
          }
          return response;
        })
        .catch(error => {
          console.error('Error loading brand by ID:', error);
          setExistingBrand(null);
          updateFields(resetBrandFields());
          setVettingRows([]);
          throw error;
        })
        .finally(() => {
          setLoadingBrandById(false);
        });
    },
    [getBrand, updateFields, loadExternalVettings],
  );

  const searchBrandByDisplayName = useCallback(
    (displayName: string) => {
      setSearchingBrand(true);
      return searchBrand({ displayName })
        .then(response => {
          const brandResponse = response.data as BrandResponse;
          const brands = brandResponse.items || [];

          if (brands.length > 0) {
            const brand = brands[0];
            setExistingBrand(brand);
            updateFields(populateFormFromBrand(brand));
            if (brand.brandId) {
              void loadExternalVettings(brand.brandId);
            }
          } else {
            setExistingBrand(null);
            updateFields(resetBrandFields());
            setVettingRows([]);
          }
          return response;
        })
        .catch(error => {
          console.error('Error searching for existing brand:', error);
          setExistingBrand(null);
          updateFields(resetBrandFields());
          throw error;
        })
        .finally(() => {
          setSearchingBrand(false);
        });
    },
    [searchBrand, updateFields, loadExternalVettings],
  );

  const searchBrandByCampaignId = useCallback(
    (tcrCampaignId: string) => {
      setSearchingBrand(true);
      return searchBrand({ tcrCampaignId })
        .then(response => {
          const brandResponse = response.data as BrandResponse;
          const brands = brandResponse.items || [];

          if (brands.length > 0) {
            const brand = brands[0];
            setExistingBrand(brand);
            updateFields(populateFormFromBrand(brand));
            if (brand.brandId) {
              void loadExternalVettings(brand.brandId);
            }
          } else {
            setExistingBrand(null);
            updateFields(resetBrandFields());
            setVettingRows([]);
          }
          return response;
        })
        .catch(error => {
          console.error('Error searching brand by tcrCampaignId:', error);
          setExistingBrand(null);
          updateFields(resetBrandFields());
          throw error;
        })
        .finally(() => {
          setSearchingBrand(false);
        });
    },
    [searchBrand, updateFields, loadExternalVettings],
  );

  useEffect(() => {
    if (formValues.displayName && formValues.displayName.trim() && !existingBrand && !loadingBrandById) {
      const timeoutId = setTimeout(() => {
        void searchBrandByDisplayName(formValues.displayName).catch(() => undefined);
      }, 1000);

      return () => clearTimeout(timeoutId);
    }
    if (!formValues.displayName || !formValues.displayName.trim()) {
      setExistingBrand(null);
      setVettingRows([]);
    }
  }, [formValues.displayName, searchBrandByDisplayName, existingBrand, loadingBrandById]);

  useEffect(() => {
    if (!company) return;

    const savedBrandInfo = company.additionalData?.brand;

    if (savedBrandInfo) {
      updateFields({
        ...populateFormFromCompanyBrandInfo(savedBrandInfo),
        displayName: savedBrandInfo.displayName || company.name || '',
        companyName: savedBrandInfo.companyName || company.name || '',
        phone: savedBrandInfo.phone || company.phoneNumbers?.[0]?.number || '',
        email: savedBrandInfo.email || company.billingEmail || '',
      });
    }

    if (company.brandId) {
      void loadBrandById(company.brandId).catch(() => undefined);
    } else if (company.tcrCampaignId) {
      void searchBrandByCampaignId(company.tcrCampaignId).catch(() => undefined);
    } else if (company.name) {
      void searchBrandByDisplayName(company.name).catch(() => undefined);
    }

    setInitialDisplayName(company.name || '');
  }, [company, loadBrandById, searchBrandByCampaignId, searchBrandByDisplayName, updateFields, loadExternalVettings]);

  const onInputChange = (field: keyof CreateBrandParams, value: string | boolean) => {
    if (field === 'ein' && typeof value === 'string') {
      updateField(field, formatEin(value));
      return;
    }

    updateField(field, value);
  };

  const onRegister = useCallback(() => {
    setSaving(true);
    setFieldErrors({});

    createBrand({
      ...formValues,
      ein: formatEin(formValues.ein),
    })
      .then(response => {
        const brandId = getBrandId(response.data);

        updateFields({
          brandId,
          status: response.data.status,
          identityStatus: response.data.identityStatus,
        });

        if (!existingBrand) {
          setExistingBrand(response.data);
        }
        if (brandId) {
          void loadExternalVettings(brandId);
        }
        toast.success('Brand registered successfully');
        refreshCompany();
      })
      .catch(error => {
        console.error('Error registering brand:', error);
        handleApiError(error, setFieldErrors, focusFieldByName);
        toast.error(existingBrand ? 'Failed to update brand' : 'Failed to register brand');
      })
      .finally(() => {
        setSaving(false);
      });
  }, [
    formValues,
    createBrand,
    updateFields,
    existingBrand,
    refreshCompany,
    setFieldErrors,
    loadExternalVettings,
  ]);

  const onSaveInfo = useCallback(() => {
    if (!company?.id) return;

    setSaving(true);
    setFieldErrors({});

    const brandInfoPayload = {
      entityType: formValues.entityType,
      displayName: formValues.displayName,
      companyName: formValues.companyName,
      ein: formatEin(formValues.ein),
      einIssuingCountry: formValues.einIssuingCountry,
      phone: formValues.phone,
      street: formValues.street,
      city: formValues.city,
      state: formValues.state,
      postalCode: formValues.postalCode,
      country: formValues.country,
      email: formValues.email,
      website: formValues.website,
      brandRelationship: formValues.brandRelationship,
      vertical: formValues.vertical,
      mock: formValues.mock,
    };

    saveCompanyBrandInfo(company.id, brandInfoPayload)
      .then(() => {
        toast.success('Brand info saved successfully');
        refreshCompany();
      })
      .catch(error => {
        console.error('Error saving brand info:', error);
        handleApiError(error, setFieldErrors, focusFieldByName);
        toast.error('Failed to save brand info');
      })
      .finally(() => {
        setSaving(false);
      });
  }, [formValues, saveCompanyBrandInfo, company?.id, refreshCompany, setFieldErrors]);

  const isFormValid = () => {
    return !!(
      formValues.entityType &&
      formValues.displayName &&
      formValues.companyName &&
      isValidEin(formValues.ein) &&
      formValues.phone &&
      formValues.street &&
      formValues.city &&
      formValues.state &&
      formValues.postalCode &&
      formValues.country &&
      formValues.email &&
      formValues.brandRelationship &&
      formValues.vertical &&
      entityTypes.length > 0 &&
      brandRelationships.length > 0 &&
      verticals.length > 0
    );
  };

  const onAccordionChange = (section: 'basicInfo' | 'addressInfo' | 'vettingDetails') => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section],
    }));
  };

  const onApplyForVetting = useCallback(() => {
    if (isSoleProprietorEntityType(formValues.entityType, entityTypes)) {
      setVettingNotAllowedDialogOpen(true);
      return;
    }

    setApplyForVettingDialogOpen(true);
  }, [
    formValues.entityType,
    entityTypes,
    setApplyForVettingDialogOpen,
    setVettingNotAllowedDialogOpen,
  ]);

  useEffect(() => {
    if (loading || loadingBrandById || searchingBrand) {
      return;
    }

    setExpandedSections({
      basicInfo: !existingBrand,
      addressInfo: !existingBrand,
      vettingDetails: !!existingBrand,
    });
  }, [loading, loadingBrandById, searchingBrand, existingBrand]);

  const onSubmitVetting = async (
    vettingType: VettingType,
    _vettingPartner: VettingPartner,
    chargeForVetting: boolean,
  ): Promise<void> => {
    const brandId = existingBrand?.brandId || formValues.brandId;
    if (!brandId) {
      toast.error('Brand ID is required to request vetting');
      return;
    }

    const payload: RequestBrandExternalVettingRequest = {
      vettingClass: vettingType,
      billToCustomer: chargeForVetting,
    };

    try {
      await requestBrandExternalVetting(brandId, payload);
      toast.success('Vetting request submitted successfully');
      await loadExternalVettings(brandId);
    } catch (error) {
      console.error('Failed to submit vetting request:', error);
      toast.error('Failed to submit vetting request');
    }
  };

  return {
    loading,
    loadingBrandById,
    searchingBrand,
    saving,
    existingBrand,
    brandRelationships,
    entityTypes,
    verticals,
    vettingRows,
    vettingLoading,
    initialDisplayName,
    expandedSections,
    applyForVettingDialogOpen,
    vettingNotAllowedDialogOpen,
    formValues,
    fieldErrors,
    showEinValidationError,
    onInputChange,
    onRegister,
    onSaveInfo,
    isFormValid,
    onAccordionChange,
    onApplyForVetting,
    setApplyForVettingDialogOpen,
    setVettingNotAllowedDialogOpen,
    onSubmitVetting,
  };
};
